import i18n from './../../shared/i18n';
import BaseModel, {defineTranslatableProperties} from "../../core/components/input-widget/BaseModel";
import DateModel from "./Date.model";
import {store} from './../../plugins/vuex/index'
import {Formatter, SYSTEM_DATE_FORMAT, SYSTEM_TIME_FORMAT} from "@/core/services/formatter.service";
import {NO_EMPTY_SPACE_AND_FORWARD_SLASH} from "@/plugins/vee-validate";
import moment from "moment";
import BaseFieldModel from "@/core/components/input-widget/BaseFieldModel";

export default class SaleModel extends BaseModel {
  uuid = null;
  sale_no = null;
  department_uuid = null;
  title = null;
  description = null;
  starting_paddle_number = null;
  currency_uuid = store.state.global.currentBranch ? store.state.global.currentBranch.currency_uuid : null;
  tax_rate = null;
  payment_terms = null;
  settlement_days = null;
  type = store.state.global.defaultAuctionType;
  display_type = null;
  status = 'draft';
  bid_cut_off_minutes = null;
  is_listed = false;
  is_published = false;
  eligible_for_online_payments = true;
  dates = [new DateModel()];
  catalogs = [];
  featured = false;
  bidding_extension = null;
  viewing_start_date = null;
  viewing_end_date = null;
  viewing_start_time = null;
  viewing_end_time = null;
  private_viewing_start_date = null;
  private_viewing_end_date = null;
  private_viewing_start_time = null;
  private_viewing_end_time = null;
  view_by_appointment = null;
  viewing_comment = null;
  clearance_start_date = null;
  clearance_end_date = null;
  clearance_start_time = null;
  clearance_end_time = null;
  strict_end_date = null;
  after_sale_end_date = null;
  strict_end_time = null;
  after_sale_end_time = null;
  clearance_comment = null;
  has_delay = null;
  lot_delay_seconds = null;
  lot_delay_batch_size = null;
  max_total_delay = null;
  buy_now_cut_off_days = null;
  hammer_tax_rate = null;
  bp_tax_rate = null;
  tax_required = true;
  require_credit_card_verification = false;
  require_identity_verification = false;
  dynamic_fields = {};
  initial_deposit_amount = null;
  increment_calculation_source = null;
  is_initial_deposit_amount = false;
  show_bidder_approval_status = false;
  manual_bidder_approval = false;
  use_images_from_lots = false;
  next_lot_no = null;
  is_paused = false;
  resume_time = false;
  hammer_target = null;
  account_managers_uuids = null;
  is_private = false;

  static TRANSLATABLE_FIELDS = ['title', 'description', 'important_information',
    'payment_information', 'viewing_information', 'collection_information', 'terms_and_conditions', 'auction_location', 'auction_contact_information'];

  static MAIN_TRANSLATABLE_FIELDS = ['title', 'description']

  static BRANCH_AUTO_TRANSLATABLE_FIELDS = [
    'important_information', 'payment_information', 'viewing_information', 'collection_information', 'terms_and_conditions', 'auction_location', 'auction_contact_information'
  ]

  attributeHints = {
    sale_no: i18n.t("If you leave this blank it will be automatically generated")
  }

  rules = {
    title: 'required',
    sale_no: `regex:${NO_EMPTY_SPACE_AND_FORWARD_SLASH}`,
    department_uuid: 'required',
    type: 'required',
    status: 'required',
    currency_uuid: 'required',
    lot_delay_batch_size: 'min_value:1|required',
    lot_delay_seconds: 'required',
    hammer_tax_rate: 'required',
    bp_tax_rate: 'required',
    telephone_bid_request_cut_off: 'min_value:0',
    commission_bid_request_cut_off: 'min_value:0',
    bidder_registration_cut_off: 'min_value:0'
  };

  attributeLabels = {
    title: i18n.t('Title'),
    sale_no: i18n.t('Sale No'),
    description: i18n.t('Description'),
    important_information: i18n.t('Important Information'),
    payment_information: i18n.t('Payment Information'),
    viewing_information: i18n.t('Viewing Information'),
    collection_information: i18n.t('Collection Information'),
    terms_and_conditions: i18n.t('Terms and Conditions'),
    auction_location: i18n.t('Auction Location'),
    auction_contact_information: i18n.t('Contact Information'),
    department_uuid: i18n.t('Department'),
    currency_uuid: i18n.t('Currency'),
    tax_rate: i18n.t('Tax Rate'),
    payment_terms: i18n.t('Payment Terms'),
    settlement_days: i18n.t('Settlement Days'),
    type: i18n.t('Type'),
    display_type: i18n.t('Type'),
    status: i18n.t('Status'),
    is_listed: i18n.t('Listed'),
    is_published: i18n.t('Published'),
    starting_paddle_number: i18n.t('Auto Bidder Starting Number'),
    dates: i18n.t('Dates'),
    branch: i18n.t('Branch'),
    category: i18n.t('Category'),
    currency: i18n.t('Currency'),
    department: i18n.t('Department'),
    bid_cut_off_minutes: i18n.t('Bid Cut Off Minutes'),
    eligible_for_online_payments: i18n.t('Eligible for Online Payments'),
    featured: i18n.t('Featured'),
    clearance_start_date: i18n.t('Clearance Start Date'),
    clearance_end_date: i18n.t('Clearance End Date'),
    clearance_start_time: i18n.t('Clearance Start Time'),
    clearance_end_time: i18n.t('Clearance End Time'),
    strict_end_date: i18n.t('Strict End Date'),
    after_sale_end_date: i18n.t('After Sale End Date'),
    strict_end_time: i18n.t('Strict End Time'),
    after_sale_end_time: i18n.t('After Sale End Time'),
    clearance_comment: i18n.t('Clearance Comment'),
    view_by_appointment: i18n.t('Viewing by Appointment'),
    viewing_start_date: i18n.t('Viewing Start Date'),
    viewing_end_date: i18n.t('Viewing End Date'),
    viewing_start_time: i18n.t('Viewing Start Time'),
    viewing_end_time: i18n.t('Viewing End Time'),
    private_viewing_start_date: i18n.t('Private Viewing Start Date'),
    private_viewing_end_date: i18n.t('Private Viewing End Date'),
    private_viewing_start_time: i18n.t('Private Viewing Start Time'),
    private_viewing_end_time: i18n.t('Private Viewing End Time'),
    viewing_comment: i18n.t('Viewing Comment'),
    has_delay: i18n.t('Stagger End Times'),
    lot_delay_seconds: i18n.t('Lot End Date Extension'),
    lot_delay_batch_size: i18n.t('Number of Lots to Extend per Batch'),
    max_total_delay: i18n.t('Max Total Delay'),
    buy_now_cut_off_days: i18n.t('Buy Now Cut Off'),
    bidding_extension: i18n.t('Bidding Extension'),
    hammer_tax_rate: i18n.t('Hammer Tax Rate'),
    bp_tax_rate: i18n.t('BP Tax Rate'),
    tax_required: i18n.t('Sales Taxes'),
    require_credit_card_verification: i18n.t('Require Credit Card Verification'),
    require_identity_verification: i18n.t('Require Identity Verification'),
    initial_deposit_amount: i18n.t('Amount'),
    is_initial_deposit_amount: i18n.t('Request Deposit'),
    show_bidder_approval_status: i18n.t('Bidder Approval Status'),
    manual_bidder_approval: i18n.t('Require Bidder Approval'),
    use_images_from_lots: i18n.t('Use Images From Lots'),
    hammer_target: i18n.t('Hammer Target'),
    account_managers_uuids: i18n.t('Account Managers'),
    is_private: i18n.t('Private Sale'),
    telephone_bid_request_cut_off: i18n.t('Telephone Bid Request Cut off (Minutes before Start Time)'),
    commission_bid_request_cut_off: i18n.t('Commission Bid Request Cut off (Minutes before Start Time)'),
    bidder_registration_cut_off: i18n.t('Bidder Registration Cut off (Minutes before Start Time)'),
    allow_webapp_condition_report_request: i18n.t('Allow Condition Report Requests Through Webapp'),
  };

  getTranslatableFieldsArrayMain(type = 'text') {
    return this.constructor.MAIN_TRANSLATABLE_FIELDS.map(key => {
      return new BaseFieldModel({type, key, label: this.attributeLabels[key], translatable: true})
    })
  }

  getBranchAutoTranslatableFieldsArray(type = 'text') {
    return this.constructor.BRANCH_AUTO_TRANSLATABLE_FIELDS.map(key => {
      return new BaseFieldModel({type, key, label: this.attributeLabels[key], translatable: true})
    })
  }

  constructor(data = {}) {
    super();
    if (data.account_managers && data.account_managers.length > 0) {
      data.account_managers_uuids = data.account_managers.map(am => am.uuid)
      console.log(data.account_managers_uuids);
    }
    Object.assign(this, {...data});

    if (this.dates.length > 0) {
      this.dates = this.dates.map(date => new DateModel(date));
    }

    if (this.status === 'published') {
      this.is_published = true;
      this.is_listed = true;
    } else if (this.status === 'listed') {
      this.is_listed = true;
    }
    if (this.uuid) {
      this.display_type = Formatter.toUpperCaseFirst(this.type);

      //get correct format for dates
      if (this.viewing_start_date) {
        const viewingStartDate = this.viewing_start_date;
        this.viewing_start_date = moment(viewingStartDate).format(SYSTEM_DATE_FORMAT);
        this.viewing_start_time = moment(viewingStartDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.viewing_end_date) {
        const viewingEndDate = this.viewing_end_date;
        this.viewing_end_date = moment(viewingEndDate).format(SYSTEM_DATE_FORMAT);
        this.viewing_end_time = moment(viewingEndDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.private_viewing_start_date) {
        const viewingStartDate = this.private_viewing_start_date;
        this.private_viewing_start_date = moment(viewingStartDate).format(SYSTEM_DATE_FORMAT);
        this.private_viewing_start_time = moment(viewingStartDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.private_viewing_end_date) {
        const viewingEndDate = this.private_viewing_end_date;
        this.private_viewing_end_date = moment(viewingEndDate).format(SYSTEM_DATE_FORMAT);
        this.private_viewing_end_time = moment(viewingEndDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.clearance_start_date) {
        const clearanceStartDate = this.clearance_start_date;
        this.clearance_start_date = moment(clearanceStartDate).format(SYSTEM_DATE_FORMAT);
        this.clearance_start_time = moment(clearanceStartDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.clearance_end_date) {
        const clearanceEndDate = this.clearance_end_date;
        this.clearance_end_date = moment(clearanceEndDate).format(SYSTEM_DATE_FORMAT);
        this.clearance_end_time = moment(clearanceEndDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.strict_end_date) {
        const strictEndDate = this.strict_end_date;
        this.strict_end_date = moment(strictEndDate).format(SYSTEM_DATE_FORMAT);
        this.strict_end_time = moment(strictEndDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.after_sale_end_date) {
        const afterSaleEndDate = this.after_sale_end_date;
        this.after_sale_end_date = moment(afterSaleEndDate).format(SYSTEM_DATE_FORMAT);
        this.after_sale_end_time = moment(afterSaleEndDate).format(SYSTEM_TIME_FORMAT);
      }
      if (this.initial_deposit_amount) {
        this.is_initial_deposit_amount = true
      }
    }



    this.initTranslatableFields(this)
    this.initializeDynamicFields()
  }

  initializeDynamicFields() {
    if (this.branch && this.branch.auction_dynamic_fields_template) {
      this.initDynamicFields(this, this.branch.auction_dynamic_fields_template[this.type]);
    } else {
      this.initDynamicFields(this, null);
    }
  }

  toJSON() {
    let data = super.toJSON();

    if (!data.clearance_end_date) {
      data.clearance_end_date = null;
      data.clearance_end_time = null;
    } else {
      let currentTzClearanceEndDate = moment(`${data.clearance_end_date} ${data.clearance_end_time}`);
      let utcClearanceEndDate = moment(currentTzClearanceEndDate.toISOString()).utc();
      data.clearance_end_date = `${utcClearanceEndDate.format(SYSTEM_DATE_FORMAT)} ${utcClearanceEndDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.clearance_start_date) {
      data.clearance_start_date = null
      data.clearance_start_time = null;
    } else {
      let currentTzClearanceStartDate = moment(`${data.clearance_start_date} ${data.clearance_start_time}`);
      let utcClearanceStartDate = moment(currentTzClearanceStartDate.toISOString()).utc();
      data.clearance_start_date = `${utcClearanceStartDate.format(SYSTEM_DATE_FORMAT)} ${utcClearanceStartDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.strict_end_date) {
      data.strict_end_date = null
      data.strict_end_time = null;
    } else {
      let currentTzStrictEndDate = moment(`${data.strict_end_date} ${data.strict_end_time}`);
      let utcStrictEndDate = moment(currentTzStrictEndDate.toISOString()).utc();
      data.strict_end_date = `${utcStrictEndDate.format(SYSTEM_DATE_FORMAT)} ${utcStrictEndDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.after_sale_end_date) {
      data.after_sale_end_date = null
      data.after_sale_end_time = null;
    } else {
      let currentTzAfterSaleEndDate = moment(`${data.after_sale_end_date} ${data.after_sale_end_time}`);
      let utcAfterSaleEndDate = moment(currentTzAfterSaleEndDate.toISOString()).utc();
      data.after_sale_end_date = `${utcAfterSaleEndDate.format(SYSTEM_DATE_FORMAT)} ${utcAfterSaleEndDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.viewing_end_date) {
      data.viewing_end_date = null
      data.viewing_end_time = null
    } else {
      let currentTzViewingEndDate = moment(`${data.viewing_end_date} ${data.viewing_end_time}`);
      let utcViewingEndDate = moment(currentTzViewingEndDate.toISOString()).utc();
      data.viewing_end_date = `${utcViewingEndDate.format(SYSTEM_DATE_FORMAT)} ${utcViewingEndDate.format(SYSTEM_TIME_FORMAT)}`;
    }
    if (!data.viewing_start_date) {
      data.viewing_start_date = null
      data.viewing_start_time = null
    } else {
      let currentTzViewingStartDate = moment(`${data.viewing_start_date} ${data.viewing_start_time}`);
      let utcViewingStartDate = moment(currentTzViewingStartDate.toISOString()).utc();
      data.viewing_start_date = `${utcViewingStartDate.format(SYSTEM_DATE_FORMAT)} ${utcViewingStartDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.private_viewing_end_date) {
      data.private_viewing_end_date = null
      data.private_viewing_end_time = null
    } else {
      let currentTzViewingEndDate = moment(`${data.private_viewing_end_date} ${data.private_viewing_end_time}`);
      let utcViewingEndDate = moment(currentTzViewingEndDate.toISOString()).utc();
      data.private_viewing_end_date = `${utcViewingEndDate.format(SYSTEM_DATE_FORMAT)} ${utcViewingEndDate.format(SYSTEM_TIME_FORMAT)}`;
    }
    if (!data.private_viewing_start_date) {
      data.private_viewing_start_date = null
      data.private_viewing_start_time = null
    } else {
      let currentTzViewingStartDate = moment(`${data.private_viewing_start_date} ${data.private_viewing_start_time}`);
      let utcViewingStartDate = moment(currentTzViewingStartDate.toISOString()).utc();
      data.private_viewing_start_date = `${utcViewingStartDate.format(SYSTEM_DATE_FORMAT)} ${utcViewingStartDate.format(SYSTEM_TIME_FORMAT)}`;
    }

    if (!data.buy_now_cut_off_days) {
      data.buy_now_cut_off_days = null
    }
    if (!data.bidding_extension) {
      data.bidding_extension = null
    }
    if (!data.max_total_delay) {
      data.max_total_delay = null
    }
    if (!data.lot_delay_seconds) {
      data.lot_delay_seconds = null
    }
    if (!data.view_by_appointment) {
      data.view_by_appointment = null
    }
    if (!data.is_initial_deposit_amount) {
      data.initial_deposit_amount = null
    }

    if (!data.account_managers_uuids) {
      data.account_managers_uuids = []
    }

    return data;
  }
}

defineTranslatableProperties(SaleModel);
